// @file Composable that prefetches whiteboard images when entering play mode.
import { FallbackAttachmentType, getFallbackAttachmentType } from '@@/bits/attachments'
import { getDisplayAttributes } from '@@/bits/beethoven'
import window from '@@/bits/global'
import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useWhiteboardModeStore } from '@@/pinia/whiteboard_mode'
import { useTldrawEditor } from '@@/vuecomposables/useTldrawEditor'
import { processedUrl } from '@padlet/vivaldi-client'
import type { PadletAttachmentShapeProps } from '@padlet/whiteboard-schema'
import type { Editor, TLPage, TLPageId, TLShape } from 'tldraw'
import { shallowRef, watch } from 'vue'

const prefetchedFrames = new Set<TLPageId>()

export const useWhiteboardPlayModePrefetchImages = (): void => {
  const tldrawEditor = shallowRef<Editor | null>(null)
  const whiteboardModeStore = useWhiteboardModeStore()

  // Store stop functions for cleanup
  let stopEditorReaction: (() => void) | null = null
  let stopModeWatcher: (() => void) | null = null

  const attemptImagePrefetch = (): void => {
    if (tldrawEditor.value == null || !whiteboardModeStore.isPlayMode) {
      prefetchedFrames.clear()
      return
    }
    prefetchImagesInAdjacentFrames(tldrawEditor.value)

    // Clean up watchers after prefetching
    if (stopEditorReaction != null) {
      stopEditorReaction()
      stopEditorReaction = null
    }

    if (stopModeWatcher != null) {
      stopModeWatcher()
      stopModeWatcher = null
    }
  }

  // Store the stop function returned by react()
  stopEditorReaction = useTldrawEditor().react((editor) => {
    if (editor == null) return
    tldrawEditor.value = editor
    attemptImagePrefetch()
  })

  // Store the stop function returned by watch()
  stopModeWatcher = watch(
    () => whiteboardModeStore.isPlayMode,
    () => attemptImagePrefetch(),
  )
}

async function prefetchAttachmentShapeImages(shapes: TLShape[]): Promise<void> {
  if (shapes.length === 0) return

  const surfacePostsStore = useSurfacePostsStore()

  // Get processed image URLs for valid image attachments
  const urlPromises = shapes.map(async (shape) => {
    const { w: width, h: height, postId } = shape.props as PadletAttachmentShapeProps
    const post = surfacePostsStore.postEntitiesById[postId]
    if (post.attachment == null) return null
    let linkAttributes = post?.attachment_link

    // attachment_link is not always present, so we fetch it from the post
    // refer to app/serializers/panko/v10/wish_serializer.rb
    if (linkAttributes == null) {
      const link = post.wish_content?.attachment_props?.url ?? post.attachment
      if (link == null || link === '') return null
      linkAttributes = await useSurfaceAttachmentsStore().fetchAndStoreLinkAttributes(link)
    }

    if (linkAttributes == null) return null

    const attachmentType = getFallbackAttachmentType(linkAttributes)

    if (attachmentType === FallbackAttachmentType.Image) {
      return processedUrl(linkAttributes.url, {
        width,
        height,
      })
    }

    // Preview other attachment types following the same logic as getBeethovenAttributes in SurfaceAttachmentPreview.vue
    const bhOptions = {
      dpr: window.devicePixelRatio,
      locale: 'en',
      width,
      height,
      use_original_image_dimensions: true,
    }

    const displayAttributes = await getDisplayAttributes(post.attachment, bhOptions)
    if (displayAttributes == null) return null
    return displayAttributes.image_url
  })

  const urls = await Promise.all(urlPromises)
  const uniqueUrls = new Set(urls.filter((url): url is string => url != null && url !== ''))

  // Prefetch each image
  Array.from(uniqueUrls).forEach((url) => {
    const image = new Image()
    image.src = url
  })
}

function prefetchBackgroundImagesForFrames(frames: TLPage[]): void {
  if (frames.length === 0) return

  // Get processed image URLs for valid image attachments
  const backgroundImageUrls = frames.map((frame) => {
    if (frame.meta.backgroundType == null || frame.meta.customBackgroundUrl == null) return null
    return frame.meta.customBackgroundUrl
  })

  const uniqueUrls = new Set(backgroundImageUrls.filter((url): url is string => url != null && url !== ''))

  // Prefetch each image
  Array.from(uniqueUrls).forEach((url) => {
    const image = new Image()
    image.src = url
  })
}

export function prefetchImagesInAdjacentFrames(editor: Editor): void {
  const pages = editor.getPages()
  const currentPageId = editor.getCurrentPageId()
  const currentPageIndex = pages.findIndex((page) => page.id === currentPageId)

  if (currentPageIndex === -1) return

  const snapshots = Object.values(editor.store.getStoreSnapshot().store)

  // Prefetch previous frame
  const previousFrame = pages[currentPageIndex - 1]
  if (previousFrame != null && !prefetchedFrames.has(previousFrame.id)) {
    const attachmentShapesInPreviousFrame = snapshots.filter(
      (shape): shape is TLShape =>
        shape.typeName === 'shape' && shape.parentId === previousFrame.id && shape.type === 'padlet-attachment-shape',
    )

    prefetchBackgroundImagesForFrames([previousFrame])
    void prefetchAttachmentShapeImages(attachmentShapesInPreviousFrame)
    prefetchedFrames.add(previousFrame.id)
  }

  // Prefetch next frame
  const nextFrame = pages[currentPageIndex + 1]
  if (nextFrame != null && !prefetchedFrames.has(nextFrame.id)) {
    const attachmentShapesInNextFrame = snapshots.filter(
      (shape): shape is TLShape =>
        shape.typeName === 'shape' && shape.parentId === nextFrame.id && shape.type === 'padlet-attachment-shape',
    )

    prefetchBackgroundImagesForFrames([nextFrame])
    void prefetchAttachmentShapeImages(attachmentShapesInNextFrame)
    prefetchedFrames.add(nextFrame.id)
  }
}
